import {
  circleAiProjectsApi,
  type CircleAiProjectsListResponse,
} from "@circle-react/api/circleAiProjectsApi";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import { infiniteQueryOptions, queryOptions } from "react-query";

export const circleAiProjectKey = createQueryKeyFactory("circle-ai-project");

interface ListParams {
  initialPage?: number;
  perPage?: number;
  query?: string;
}

const DEFAULT_PER_PAGE = 20;
const DEFAULT_QUERY = "";
const FILES_PER_PAGE = 100;

export const circleAiProjectQueries = {
  list: ({
    query = DEFAULT_QUERY,
    perPage = DEFAULT_PER_PAGE,
    initialPage = 1,
  }: ListParams = {}) =>
    infiniteQueryOptions({
      initialPageParam: initialPage,
      queryKey: circleAiProjectKey.key("list", query, perPage, initialPage),
      queryFn: ({ pageParam = initialPage }: { pageParam?: number }) =>
        circleAiProjectsApi.list({
          params: {
            page: pageParam > 0 ? pageParam : 1,
            per_page: perPage,
            ...(query && { query }),
          },
        }),
      getNextPageParam: (lastPage: CircleAiProjectsListResponse) => {
        const hasMore =
          lastPage.pagination.page * lastPage.pagination.per_page <
          lastPage.pagination.total_count;
        return hasMore ? lastPage.pagination.page + 1 : undefined;
      },
    }),
  archivedList: ({
    query = DEFAULT_QUERY,
    perPage = DEFAULT_PER_PAGE,
    initialPage = 1,
  }: ListParams = {}) =>
    infiniteQueryOptions({
      initialPageParam: initialPage,
      queryKey: circleAiProjectKey.key(
        "archivedList",
        query,
        perPage,
        initialPage,
      ),
      queryFn: ({ pageParam = initialPage }: { pageParam?: number }) =>
        circleAiProjectsApi.archivedList({
          params: {
            page: pageParam > 0 ? pageParam : 1,
            per_page: perPage,
            ...(query && { query }),
          },
        }),
      getNextPageParam: (lastPage: CircleAiProjectsListResponse) => {
        const hasMore =
          lastPage.pagination.page * lastPage.pagination.per_page <
          lastPage.pagination.total_count;
        return hasMore ? lastPage.pagination.page + 1 : undefined;
      },
    }),
  detail: (uuid: string) =>
    queryOptions({
      queryKey: circleAiProjectKey.key("detail", uuid),
      queryFn: () => circleAiProjectsApi.show({ uuid }),
    }),
  files: (uuid: string) =>
    queryOptions({
      queryKey: circleAiProjectKey.key("files", uuid),
      queryFn: () =>
        circleAiProjectsApi.filesList({
          uuid,
          params: { per_page: FILES_PER_PAGE },
        }),
    }),
};

export const circleAiProjectInvalidations = {
  all: () =>
    queryClient.invalidateQueries({ queryKey: circleAiProjectKey.root() }),
  files: (uuid: string) =>
    queryClient.invalidateQueries({
      queryKey: circleAiProjectKey.key("files", uuid),
    }),
};
